import { useMemo, useState } from "react";
import { ArrowUp, ArrowDown, ArrowUpDown, Filter, Check } from "lucide-react";
import { TableHead } from "./ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ScrollArea } from "./ui/scroll-area";
import { getDistinctValues } from "../lib/tableTools";

/**
 * Iter-69: SortableTableHead — drop-in replacement for <TableHead>.
 *
 * Renders a sortable column header with an optional filter dropdown
 * (categorical multi-select). Both interactions are opt-in via props.
 *
 * Props:
 *  - column: column config object from useTableControls (key, label,
 *    accessor, sortable, filterable, filterFormatter, etc.)
 *  - sortKey: current active sort column key (from useTableControls)
 *  - sortDir: current sort direction ("none" | "asc" | "desc")
 *  - onToggleSort: () => void — called when user clicks header
 *  - rows: ALL rows passed to useTableControls (BEFORE filter/sort) so
 *    the filter dropdown can list every distinct value, not just the
 *    currently-visible ones.
 *  - selectedFilters: Set<string> | undefined — current filter for THIS column
 *  - onChangeFilter: (Set<string>) => void
 *  - className, ...rest: forwarded to <TableHead>
 */
const SortableTableHead = ({
  column,
  sortKey,
  sortDir,
  onToggleSort,
  rows = [],
  selectedFilters,
  onChangeFilter,
  className = "",
  children,
  ...rest
}) => {
  const isActive = sortKey === column.key && sortDir !== "none";
  const Icon = isActive ? (sortDir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;

  return (
    <TableHead className={`align-middle ${className}`} {...rest}>
      <div className="flex items-center gap-1.5">
        {column.sortable ? (
          <button
            type="button"
            onClick={onToggleSort}
            className="inline-flex items-center gap-1 hover:text-primary transition-colors group"
            data-testid={`sort-${column.key}`}
            aria-label={`Sort by ${column.label}`}
          >
            <span>{children ?? column.label}</span>
            <Icon
              className={`w-3 h-3 transition-opacity ${
                isActive ? "opacity-100 text-primary" : "opacity-40 group-hover:opacity-80"
              }`}
            />
          </button>
        ) : (
          <span>{children ?? column.label}</span>
        )}
        {column.filterable && (
          <FilterPopover
            column={column}
            rows={rows}
            selected={selectedFilters}
            onChange={onChangeFilter}
          />
        )}
      </div>
    </TableHead>
  );
};

const FilterPopover = ({ column, rows, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const distinct = useMemo(
    () => getDistinctValues(rows, column.accessor, column.filterFormatter),
    [rows, column]
  );

  const isActive = selected && selected.size > 0;
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return distinct;
    return distinct.filter((v) => v.toLowerCase().includes(q));
  }, [distinct, query]);

  const allSelected = isActive && selected.size === distinct.length;

  const toggleValue = (val) => {
    const next = new Set(selected || []);
    if (next.has(val)) next.delete(val);
    else next.add(val);
    onChange?.(next);
  };

  const clearAll = () => {
    onChange?.(new Set());
    setQuery("");
  };

  const selectAll = () => {
    onChange?.(new Set(distinct));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={`h-6 w-6 rounded-md ${
            isActive ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-foreground"
          }`}
          data-testid={`filter-${column.key}`}
          aria-label={`Filter by ${column.label}`}
        >
          <Filter className="w-3 h-3" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="font-semibold uppercase tracking-wider text-muted-foreground">
              Filter {column.label}
            </span>
            {isActive && (
              <button
                type="button"
                onClick={clearAll}
                className="text-primary hover:underline text-xs"
                data-testid={`filter-${column.key}-clear`}
              >
                Clear
              </button>
            )}
          </div>
          {distinct.length > 6 && (
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="h-7 text-xs"
              data-testid={`filter-${column.key}-search`}
            />
          )}
          <div className="flex items-center justify-between text-[10px] text-muted-foreground">
            <span>
              {isActive ? `${selected.size} of ${distinct.length} selected` : `${distinct.length} values`}
            </span>
            {!allSelected && (
              <button
                type="button"
                onClick={selectAll}
                className="hover:text-foreground"
              >
                Select all
              </button>
            )}
          </div>
          <ScrollArea className="max-h-56">
            <div className="space-y-0.5">
              {filtered.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-2">No matches</p>
              ) : (
                filtered.map((val) => {
                  const checked = !!selected?.has(val);
                  return (
                    <button
                      type="button"
                      key={val}
                      onClick={() => toggleValue(val)}
                      className={`w-full flex items-center gap-2 px-2 py-1 rounded-md text-xs text-left hover:bg-secondary/50 transition-colors ${
                        checked ? "bg-primary/10 text-foreground" : ""
                      }`}
                      data-testid={`filter-${column.key}-opt-${val.replace(/\s+/g, "_")}`}
                    >
                      <span className={`flex-shrink-0 w-3.5 h-3.5 rounded-sm border ${
                        checked ? "bg-primary border-primary" : "border-white/20"
                      } flex items-center justify-center`}>
                        {checked && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
                      </span>
                      <span className="truncate">{val}</span>
                    </button>
                  );
                })
              )}
            </div>
          </ScrollArea>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SortableTableHead;
